import React from "react";
import { motion } from "framer-motion";

/**
 * MaskReveal — clip-path masked line reveal from bottom.
 * Splits text into words (word-safe) and staggers each with an
 * inset-clip mask that opens upward. Use for hero display type.
 */
export function MaskReveal({ text, as = "span", delay = 0, stagger = 0.06, className = "", italic = false }) {
  const words = String(text).split(" ");
  const Tag = motion[as] || motion.span;
  return (
    <Tag className={`inline-block ${className}`} style={{ overflow: "hidden" }}>
      {words.map((w, i) => (
        <span key={i} style={{ display: "inline-block", overflow: "hidden", verticalAlign: "top" }}>
          <motion.span
            style={{ display: "inline-block", fontStyle: italic ? "italic" : undefined }}
            initial={{ y: "110%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 0.9, delay: delay + i * stagger, ease: [0.22, 1, 0.36, 1] }}
          >
            {w}
          </motion.span>
          {i < words.length - 1 && <span>&nbsp;</span>}
        </span>
      ))}
    </Tag>
  );
}

/**
 * FadeUp — scroll-into-view fade-up. Uses whileInView with once=true.
 */
export function FadeUp({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * ClipReveal — image reveal with a clip-path wipe from bottom.
 */
export function ClipReveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ clipPath: "inset(100% 0 0 0)" }}
      whileInView={{ clipPath: "inset(0% 0 0 0)" }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.1, delay, ease: [0.77, 0, 0.175, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * GoldLineDraw — animated gold underline that draws horizontally on scroll.
 */
export function GoldLineDraw({ className = "" }) {
  return (
    <motion.span
      className={`block h-[3px] rounded-full ${className}`}
      style={{ background: "var(--mv-gold)", transformOrigin: "left center" }}
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    />
  );
}
